
// Copyright 2012-2020 The MathWorks, Inc.
define([
    'dojo/_base/array',
    'dojo/_base/declare',
    'dojo/_base/lang'
], function (
    array,
    declare,
    lang
) {
    /**
     * The FaultManager is responsible for handling transport and container level faults. These
     * faults are given to the fault manager through the "handleFault" method. The fault is passed
     * to registered fault handlers so that other parts of the application can have a chance to
     * clean up or perform other actions.
     */
    return declare([], {
        constructor: function (args) {
            args = args || {};
            this.faultHandlers = args.faultHandlers || {};
            this.topLevelHandlers = args.topLevelHandlers || [];
            this.endPointLevelHandlers = args.endPointLevelHandlers || {};
        },

        /**
         * Register a handler function which is called to handle container or transport faults
         * related to the specified request message type. If a fault occurs while processing a
         * transaction with the specified type of message in it, the handler will be called.
         * @param messageType Type of message to register a fault handler for.
         * @param handlerFunction Function object to call. This will be passed the request context
         * and the fault.
         * @param scope Optional scope to bind the function to.
         */
        registerFaultHandler: function (messageType, handlerFunction, scope) {
            if (scope) {
                handlerFunction = lang.hitch(scope, handlerFunction);
            }
            this.faultHandlers[messageType] = handlerFunction;
        },

        /**
         * Deregisters a message fault handler.
         * @param messageType Type of message.
         */
        deregisterFaultHandler: function (messageType) {
            delete this.faultHandlers[messageType];
        },

        /**
         * Register a handler function which is called to handle container or transport faults for.
         * @param faultMatcher A query string to match against the fault code. This supports "*" for
         * wild carding and "||" for multiple conditions.
         * @param handlerFunction Function object to call. This will be passed the request context
         * and the fault object. If no handler is specified, the matching fault will be ignored.
         * @param scope Optional scope to bind the function to.
         * @return Returns a token which can be used to deregister the handler.
         */
        registerTopLevelFaultHandler: function (faultMatcher, handlerFunction, scope) {
            var handler;

            if (scope) {
                handlerFunction = lang.hitch(scope, handlerFunction);
            }

            if (lang.isString(faultMatcher)) {
                if (faultMatcher === '*') {
                    handler = { matcher: faultMatcher, handler: handlerFunction };
                    this.defaultTopLevelHandler = handlerFunction;
                    return handler;
                }

                faultMatcher = this._constructMatcherFromQuery(faultMatcher);
            }

            handler = { matcher: faultMatcher, handler: handlerFunction };
            this.topLevelHandlers.push(handler);
            return handler;
        },

        registerEndpointLevelFaultHandler: function (endPoint, faultMatcher, handlerFunction,
            scope) {
            var handler;

            if (scope) {
                handlerFunction = lang.hitch(scope, handlerFunction);
            }

            if (lang.isString(faultMatcher)) {
                faultMatcher = this._constructMatcherFromQuery(faultMatcher);
            }

            handler = { matcher: faultMatcher, handler: handlerFunction };
            if (!this.endPointLevelHandlers[endPoint]) {
                this.endPointLevelHandlers[endPoint] = [];
            }
            this.endPointLevelHandlers[endPoint].push(handler);
            return handler;
        },

        /**
         * Deregisters a message fault handler.
         * @param registrationToken Token returned by the registration function.
         */
        deregisterTopLevelFaultHandler: function (registrationToken) {
            var index = this.topLevelHandlers.indexOf(registrationToken);
            if (index > -1) {
                this.topLevelHandlers.splice(index, 1);
            } else if (registrationToken.matcher === '*') {
                delete this.defaultTopLevelHandler;
            }
        },

        /**
         * Construct a matcher function based on a query string which includes wild card(s).
         * @param query A query string.
         */
        _constructMatcherFromQuery: function (query) {
            var terms = query.split('||');
            var regexs = [];
            terms.forEach(function (term) {
                term = lang.trim(term);
                term = term.replace(/([.])/g, '\\$1');
                term = term.replace(/\*/g, '.*');
                regexs.push(new RegExp('^' + term + '$'));
            });

            return function (fault) {
                return array.some(regexs, function (regex) {
                    return regex.test(fault.faultCode || fault.code || '');
                });
            };
        },

        /**
         * Method used by the ResponseManager to pass off handling of any faults. All registered
         * fault handlers with messages in the request will be called.
         * @param transaction Transaction containing the fault.
         */
        handleFault: function (transaction) {
            var type;

            var handleWithClassifier = function (type, message) {
                var typeWithClassifier;
                var classifier = transaction.getMessageClassifier(message);
                if (classifier) {
                    typeWithClassifier = [type, classifier].join(':');
                    if (this.faultHandlers[typeWithClassifier]) {
                        this.faultHandlers[typeWithClassifier](transaction.fault,
                            transaction.context);
                    }
                }
            };

            for (type in transaction.requestMessages) {
                if (transaction.requestMessages.hasOwnProperty(type)) {
                    if (this.faultHandlers[type]) {
                        this.faultHandlers[type](transaction.fault, transaction.context);
                    }

                    var messages = transaction.requestMessages[type];
                    if (Array.isArray(messages)) {
                        messages.forEach(lang.hitch(this, handleWithClassifier, type));
                    }
                }
            }

            if (this.faultHandlers['*']) {
                this.faultHandlers['*'](transaction.fault, transaction.context);
            }

            if (this.endPointLevelHandlers[transaction.endPointName]) {
                this._handleEndpointLevelFault(transaction);
            } else {
                this._handleTopLevelFault(transaction);
            }
        },

        _handleEndpointLevelFault: function (transaction) {
            var isFaultHandled = false;
            // find all of the handlers which match the fault.
            var matching = array.filter(this.endPointLevelHandlers[transaction.endPointName],
                function (matcherAndHandler) {
                    return matcherAndHandler.matcher.call(this, transaction.fault, transaction.context);
                }, this);

            if (matching.length > 0 && matching[0].handler) {
                isFaultHandled = true;
                matching[0].handler.call(this, transaction.fault, transaction.context, transaction);
            }
            return isFaultHandled;
        },

        _handleTopLevelFault: function (transaction) {
            // find all of the handlers which match the fault.
            var matching = array.filter(this.topLevelHandlers, function (matcherAndHandler) {
                return matcherAndHandler.matcher.call(this, transaction.fault, transaction.context);
            }, this);

            // todo: come up with some smarter scheme to handle the case where multiple matchers fit
            // a fault Use the first matching handler, or the default handler if none matched.
            var handler = matching.length > 0 ? matching[0].handler : this.defaultTopLevelHandler;
            if (handler) {
                handler.call(this, transaction.fault, transaction.context, transaction);
            }
        },

        /**
         * Method used by the Remote layer to handle all non transaction faults. Used
         * by IframeProxy load timeout
         * @param fault The fault object
         */
        handleNonTransactionFault: function (fault) {
            var matching = array.filter(this.topLevelHandlers, function (matcherAndHandler) {
                return matcherAndHandler.matcher.call(this, fault);
            }, this);
            var handler = matching.length > 0 ? matching[0].handler : this.defaultTopLevelHandler;
            if (handler) {
                handler.call(this, fault);
            }
        }
    });
});
